
import Vue from "vue";
import TitledSection from "@/shared/components/TitledSection.vue";
import LabeledItem from "@/shared/components/LabeledItem.vue";
import FileUpload from "@/features/Documents/FileUpload.vue";
import FileList from "@/features/Documents/FileList.vue";
import ActionList from "@/features/Actions/ActionList.vue";
import { ActionType } from "@/features/Actions/Action";
import CommentSection from "@/features/Comments/CommentSection.vue";
import { CommentType } from "@/features/Comments/Comment";
import EventsTable from "@/shared/components/EventsTable.vue";
import Incident, { UIIncidentSeverity } from "@/features/Incidents/Incident";
import AppError from "@/shared/components/AppError.vue";
import { formatDatepickerDate, parseDateAsUTC } from "@/shared/utils/common_utils";

export default Vue.extend({
    name: "IncidentDetails",
    components: {
        TitledSection,
        LabeledItem,
        FileUpload,
        FileList,
        AppError,
        ActionList,
        CommentSection,
        EventsTable,
    },
    data: () => ({
        UIIncidentSeverity: UIIncidentSeverity,
        dataIncident: null! as Incident,
        incidentValid: false,
        dateMenu: false,
        severityItems: Object.values(UIIncidentSeverity)
            .filter(v => !isNaN(Number(v)))
            .map((value, index) => ({ id: value, text: UIIncidentSeverity[index] })),
        severityRules: [(v: number) => !isNaN(v) || "Choose severity"],
        dateRules: [(v: string) => (v || "").length > 0 || "Select a date", (v: string) => !isNaN(Date.parse(v)) || "Invalid date"],
        notesRules: [(v: string) => (v || "").length > 0 || "Enter notes"],
        errorMessage: "",
        showError: false,
        editMode: false,
        dataIsNewIncident: false,
        actionType: ActionType.ComplianceIncidentAction,
        commentType: CommentType.IncidentComment,
    }),
    props: {
        incidentID: {
            type: String,
            required: true,
        },
        isNewIncident: {
            type: Boolean,
            required: true,
        },
    },
    async mounted() {
        await this.getIncident(this.incidentID);
        this.dataIsNewIncident = this.isNewIncident;
        this.editMode = this.dataIsNewIncident;
    },
    watch: {
        // When validity state changes, emit an event so the parent can update its validity state
        incidentValid(isValid: boolean) {
            this.$emit("validityChanged", isValid);

            if (!isValid) {
                // update display to show invalid fields
                (this.$refs.incidentForm as any).validate();
            }
        },
        validateIncidentForm(newValue: boolean) {
            if (newValue) {
                (this.$refs.incidentForm as any).validate();
            }
        },
        incidentID(newValue: string) {
            if (newValue) {
                this.getIncident(newValue);
            }
        },
    },
    computed: {
        canViewAuditableEvents(): boolean {
            if (this.isNewIncident) {
                return false;
            }

            return this.$incidentAuthorizationService.canViewIncidentAuditableEvents();
        },
    },
    methods: {
        async getIncident(id: string) {
            try {
                this.dataIncident = await this.$incidentService.getIncidentFromServer(id);
            } catch {
                this.errorMessage = "Failed to fetch incident";
                this.showError = true;
            }
        },
        editIncident() {
            this.editMode = true;
        },
        async saveIncident() {
            if (this.incidentValid) {
                try {
                    await this.$incidentService.updateIncident(this.dataIncident);
                    this.editMode = false;
                    this.dataIsNewIncident = false;

                    // Re-fetch incident
                    this.$emit("incident-updated");
                    await this.getIncident(this.incidentID);
                } catch {
                    this.errorMessage = "Failed to save incident";
                    this.showError = true;
                }
            }
        },
        async cancelIncident() {
            // If it's a new incident, delete the one we just made.
            if (this.dataIsNewIncident) {
                try {
                    await this.$incidentService.deleteIncident(this.incidentID);
                    this.$emit("incident-updated");
                    this.$emit("close-pane");
                } catch {
                    this.errorMessage = "Failed to cancel incident";
                    this.showError = true;
                }
            } else {
                this.$emit("close-pane");
            }
        },
        onIncidentChanged(fieldToUpdate: string, newValue: any) {
            this.$emit("updateIncident", fieldToUpdate, newValue);
        },
        documentUploaded(documentMetadataID: string): void {
            this.dataIncident.documentMetadataIDs.push(documentMetadataID);
            this.onIncidentChanged("documentMetadataIDs", this.dataIncident.documentMetadataIDs);
        },
        documentRemoved(documentMetadataID: string): void {
            this.dataIncident.documentMetadataIDs = this.dataIncident.documentMetadataIDs.filter(id => id != documentMetadataID);
            this.onIncidentChanged("documentMetadataIDs", this.dataIncident.documentMetadataIDs);
        },
        formatDatepickerDateForDisplay(dateString: string) {
            const date = parseDateAsUTC(dateString);
            return formatDatepickerDate(date);
        },
    },
});
